import React, { useState, useEffect } from 'react';
import {
    ArrowLeft, Search, Calendar, Clock, CheckCircle, XCircle,
    RefreshCw, Filter, Download, Eye, AlertCircle, Loader2,
    RotateCcw, CalendarRange, Phone, User, Hash, List
} from 'lucide-react';
import WhatsAppDetails from './WhatsAppDetails';
import {
    fetchWhatsAppAppNames,
    fetchWhatsAppVendors,
    fetchWhatsAppHistoryFilter
} from './whatsappConfigService';
import { useWebSocket } from '../../websocket/WebSocketContext';
import AnimatedBackground from '../Background';
import { apiFetch } from '../../api';
import ExportBlockedModal from '../Exportblockedmodal';

export default function WhatsAppHistory({ onBack, setError, API_BASE_URL, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const [wpData, setWpData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    // Export-blocked modal
    const [showExportBlocked, setShowExportBlocked] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [referenceSearch, setReferenceSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [appNames, setAppNames] = useState([]);
    const [vendorNames, setVendorNames] = useState([]);
    const [selectedApp, setSelectedApp] = useState('ALL');
    const [selectedVendorName, setSelectedVendorName] = useState('ALL');
    const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [selectedWp, setSelectedWp] = useState(null);
    const [selectedItems, setSelectedItems] = useState(new Set());

    /* ── Data fetchers ─────────────────────────────────────────── */
    const fetchAppNamesData = async () => {
        setIsAppNamesLoading(true);
        try {
            const names = await fetchWhatsAppAppNames({ deliveryMode, client, loginId });
            setAppNames(['ALL', ...names]);
            setSelectedApp(prev => prev || 'ALL');
        } catch {
            setAppNames(['ALL']);
            setSelectedApp('ALL');
        } finally {
            setIsAppNamesLoading(false);
        }
    };

    const fetchVendorNamesData = async () => {
        try {
            const vendors = await fetchWhatsAppVendors({ deliveryMode, client, loginId });
            setVendorNames(['ALL', ...vendors]);
            setSelectedVendorName(prev => prev || 'ALL');
        } catch {
            setVendorNames(['ALL']);
            setSelectedVendorName('ALL');
        }
    };

    const dateToMs = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00').getTime().toString() : '';
    const endDateToMs = (dateStr) => dateStr ? new Date(dateStr + 'T23:59:59').getTime().toString() : '';

    const fetchWpHistory = async (page = currentPage, size = pageSize) => {
        setIsLoading(true);
        const filterPayload = { pageNo: page - 1, pageSize: size };
        if (searchTerm.trim()) filterPayload.searchText = searchTerm.trim();
        if (referenceSearch.trim()) filterPayload.referenceIdOrName = referenceSearch.trim();
        if (selectedApp && selectedApp !== 'ALL') filterPayload.appName = selectedApp;
        if (selectedVendorName && selectedVendorName !== 'ALL') filterPayload.vendorName = selectedVendorName;
        if (statusFilter) filterPayload.status = statusFilter;
        if (startDate) filterPayload.startTimeMs = dateToMs(startDate);
        if (endDate) filterPayload.endTimeMs = endDateToMs(endDate);

        try {
            const result = await fetchWhatsAppHistoryFilter({ deliveryMode, client, loginId }, filterPayload);
            setWpData(result.Response || []);
            setTotalCount(result.TOTAL_COUNT || 0);
        } catch (err) {
            if (setError) setError('Failed to fetch WhatsApp history: ' + err.message);
            setWpData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    /* ── Export ────────────────────────────────────────────────── */
    const handleExport = async () => {
        // Block export in socket/stream mode — show modal instead
        if (deliveryMode === 'socket') {
            setShowExportBlocked(true);
            return;
        }

        if (!startDate || !endDate) {
            setError('Please select both start and end dates for export');
            return;
        }

        setIsExporting(true);
        try {
            const filterPayload = { pageNo: 0, pageSize: 1000 };
            if (searchTerm.trim()) filterPayload.searchText = searchTerm.trim();
            if (referenceSearch.trim()) filterPayload.referenceIdOrName = referenceSearch.trim();
            if (selectedApp && selectedApp !== 'ALL') filterPayload.appName = selectedApp;
            if (selectedVendorName && selectedVendorName !== 'ALL') filterPayload.vendorName = selectedVendorName;
            if (statusFilter) filterPayload.status = statusFilter;
            if (startDate) filterPayload.startTimeMs = dateToMs(startDate);
            if (endDate) filterPayload.endTimeMs = endDateToMs(endDate);

            const res = await apiFetch(`/whatsapp/filter?export=true`, {
                method: 'POST',
                body: JSON.stringify(filterPayload),
            });

            if (!res.ok) throw new Error(`Export failed: ${res.status} ${res.statusText}`);

            const data = await res.json();
            if (data.STATUS === 'SUCCESS' && data.Response) {
                const byteCharacters = atob(data.Response);
                const byteArray = new Uint8Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
                const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `whatsapp-history-${startDate}-to-${endDate}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Export failed: Invalid response format');
            }
        } catch (error) {
            setError('Failed to export data: ' + error.message);
        } finally {
            setIsExporting(false);
        }
    };

    /* ── Handlers ──────────────────────────────────────────────── */
    const handleResetFilters = () => {
        setSearchTerm(''); setReferenceSearch(''); setStartDate(''); setEndDate('');
        setStatusFilter(''); setSelectedApp('ALL'); setSelectedVendorName('ALL');
        setSelectedItems(new Set()); setCurrentPage(1); setPageSize(10);
    };

    useEffect(() => {
        if (!selectedApp) return;
        const delay = searchTerm || referenceSearch ? 500 : 0;
        const timer = setTimeout(() => fetchWpHistory(currentPage, pageSize), delay);
        return () => clearTimeout(timer);
    }, [searchTerm, referenceSearch, startDate, endDate, statusFilter, selectedApp, selectedVendorName, currentPage, pageSize]);

    useEffect(() => { fetchAppNamesData(); fetchVendorNamesData(); }, []);

    const handleRefresh = () => { fetchWpHistory(currentPage, pageSize); fetchAppNamesData(); fetchVendorNamesData(); };

    const handlePageSizeChange = (e) => { setIsLoading(true); setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); };
    const handlePreviousPage = () => { if (currentPage > 1 && !isLoading) { setIsLoading(true); setCurrentPage(p => p - 1); } };
    const handleNextPage = () => { if (wpData.length >= pageSize && !isLoading) { setIsLoading(true); setCurrentPage(p => p + 1); } };

    const getStatusColor = (status) => {
        if (status === 'SUCCESS') return 'text-green-400';
        if (status === 'FAILED') return 'text-red-400';
        return 'text-yellow-400';
    };
    const getStatusIcon = (status) => {
        if (status === 'SUCCESS') return CheckCircle;
        if (status === 'FAILED') return XCircle;
        return AlertCircle;
    };
    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        return phone.length === 10 ? phone.replace(/(\d{5})(\d{5})/, '$1 $2') : phone;
    };

    const handleItemSelect = (id) => {
        const s = new Set(selectedItems);
        s.has(id) ? s.delete(id) : s.add(id);
        setSelectedItems(s);
    };
    const handleSelectAll = () => {
        setSelectedItems(selectedItems.size === wpData.length ? new Set() : new Set(wpData.map(i => i._id)));
    };

    // Export button is visually "enabled" but will show modal in socket mode
    const isExportEnabled = startDate && endDate && !isExporting;

    /* ══════════════════════════════════════════════════════════════ */
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8]">
            <AnimatedBackground />

            {/* Export-blocked modal — shown when in socket mode */}
            <ExportBlockedModal
                isOpen={showExportBlocked}
                onClose={() => setShowExportBlocked(false)}
                feature="WhatsApp Export"
            />

            {/* Header */}
            <header className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={onBack}
                                className="flex items-center space-x-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span>Back to Dashboard</span>
                            </button>
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">WhatsApp History</h1>
                                    <p className="text-sm text-white/60">WhatsApp Message History</p>
                                </div>
                            </div>
                        </div>

                        {/* Export button — always clickable; socket mode triggers modal */}
                        <button
                            onClick={handleExport}
                            disabled={!isExportEnabled && deliveryMode !== 'socket'}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${deliveryMode === 'socket'
                                    ? 'bg-orange-500/20 text-orange-200 hover:bg-orange-500/30 cursor-pointer'
                                    : isExportEnabled
                                        ? 'bg-green-500/20 text-green-200 hover:bg-green-500/30'
                                        : 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            {isExporting
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Download className="w-4 h-4" />
                            }
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                            {deliveryMode === 'socket' && (
                                <span className="ml-1 text-[10px] font-bold bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full border border-orange-500/30">
                                    API only
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Filters */}
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Search */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search WhatsApp messages..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                            </div>
                        </div>

                        {/* Reference */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Reference ID or Name</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input type="text" value={referenceSearch} onChange={e => setReferenceSearch(e.target.value)}
                                    placeholder="Search by reference..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                            </div>
                        </div>

                        {/* Start Date */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Start Date</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                            </div>
                        </div>

                        {/* End Date */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">End Date</label>
                            <div className="relative">
                                <CalendarRange className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Status</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500">
                                    <option value="" className="bg-slate-800">All Status</option>
                                    <option value="SUCCESS" className="bg-slate-800">Success</option>
                                    <option value="FAILED" className="bg-slate-800">Failed</option>
                                    <option value="PENDING" className="bg-slate-800">Pending</option>
                                </select>
                            </div>
                        </div>

                        {/* App Name */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">App Name</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select value={selectedApp} onChange={e => setSelectedApp(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500">
                                    {appNames.length === 0
                                        ? <option className="bg-slate-800" disabled>Loading...</option>
                                        : appNames.map((app, i) => <option key={i} value={app} className="bg-slate-800">{app === 'ALL' ? 'All Apps' : app}</option>)
                                    }
                                </select>
                            </div>
                        </div>

                        {/* Vendor */}
                        <div>
                            <label className="block text-white/80 text-sm font-medium mb-2">Vendor Name</label>
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                                <select value={selectedVendorName} onChange={e => setSelectedVendorName(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500">
                                    {vendorNames.length === 0
                                        ? <option className="bg-slate-800" disabled>Loading...</option>
                                        : vendorNames.map((v, i) => <option key={i} value={v} className="bg-slate-800">{v === 'ALL' ? 'All Vendors' : v}</option>)
                                    }
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Action row */}
                    <div className="flex items-center space-x-2 mt-4">
                        <button onClick={handleRefresh} disabled={isLoading}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                        <button onClick={handleResetFilters}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 text-orange-200 rounded-lg hover:bg-orange-500/30 transition-all duration-200">
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset</span>
                        </button>
                    </div>

                    {/* Export info notice */}
                    {deliveryMode !== 'socket' && !isExportEnabled && (
                        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0" />
                            <span className="text-yellow-200 text-sm">Select both start and end dates to enable export</span>
                        </div>
                    )}
                    {deliveryMode === 'socket' && (
                        <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-orange-400 shrink-0" />
                            <span className="text-orange-200 text-sm">
                                You're in <span className="font-bold text-orange-300">Stream Mode</span> — export requires <span className="font-bold text-indigo-300">API Mode</span>. Click Export to learn more.
                            </span>
                        </div>
                    )}
                </div>

                {/* Results */}
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <h2 className="text-xl font-bold text-white">
                                    WhatsApp Messages
                                    <span className="ml-3 text-sm text-white/60">
                                        Showing {wpData.length} record{wpData.length !== 1 ? 's' : ''}
                                    </span>
                                </h2>
                                {wpData.length > 0 && (
                                    <div className="flex items-center space-x-2">
                                        <input type="checkbox"
                                            checked={selectedItems.size === wpData.length && wpData.length > 0}
                                            onChange={handleSelectAll}
                                            className="rounded border-white/20 bg-white/10 text-teal-500 focus:ring-teal-500" />
                                        <span className="text-white/60 text-sm">
                                            {selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select all'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-teal-400 mr-3" />
                            <span className="text-white/80">Loading WhatsApp history…</span>
                        </div>
                    ) : wpData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <AlertCircle className="w-12 h-12 text-white/40 mb-4" />
                            <p className="text-white/60 text-lg">No WhatsApp messages found</p>
                            <p className="text-white/40 text-sm">Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/10">
                            {wpData.map((wp) => {
                                const StatusIcon = getStatusIcon(wp.STATUS);
                                const statusColorClass = getStatusColor(wp.STATUS);
                                return (
                                    <div key={wp._id} className="px-6 py-4 hover:bg-white/5 transition-all duration-200">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0 pt-1">
                                                <input type="checkbox" checked={selectedItems.has(wp._id)}
                                                    onChange={() => handleItemSelect(wp._id)}
                                                    className="rounded border-white/20 bg-white/10 text-teal-500 focus:ring-teal-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center shrink-0">
                                                        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                                        </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <h3 className="text-white font-semibold">{wp.Type || 'SINGLE'} Message</h3>
                                                            {wp.campaignId && (
                                                                <span className="px-2 py-0.5 bg-teal-500/20 text-teal-200 text-xs rounded-full">
                                                                    Campaign: {wp.campaignId}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {wp.attributeDetails?.length > 0 && (
                                                            <p className="text-white/60 text-sm truncate">
                                                                {wp.attributeDetails.map(a => `${a.attributeName}: ${a.attributeValue}`).join(' | ')}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center space-x-2 shrink-0">
                                                        <StatusIcon className={`w-5 h-5 ${statusColorClass}`} />
                                                        <span className={`text-sm font-medium ${statusColorClass}`}>{wp.STATUS}</span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Mobile Number</p>
                                                        <div className="flex items-center space-x-2">
                                                            <Phone className="w-4 h-4 text-white/50" />
                                                            <p className="text-white/80 text-sm font-mono">{formatPhoneNumber(wp.mobileNo) || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">App Name</p>
                                                        <div className="flex items-center space-x-2">
                                                            <User className="w-4 h-4 text-white/50" />
                                                            <p className="text-white/80 text-sm">{wp.appName || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Vendor</p>
                                                        <div className="flex items-center space-x-2">
                                                            <Hash className="w-4 h-4 text-white/50" />
                                                            <p className="text-white/80 text-sm">{wp.vendorName || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between mt-3">
                                                    <div className="flex items-center space-x-4 text-white/60 text-sm">
                                                        <div className="flex items-center space-x-1">
                                                            <Clock className="w-4 h-4" />
                                                            <span>{wp.creation_time_str}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <Calendar className="w-4 h-4" />
                                                            <span>{wp.date}</span>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => setSelectedWp(wp)}
                                                        className="flex items-center space-x-1 px-3 py-1 bg-teal-500/20 text-teal-200 rounded-lg hover:bg-teal-500/30 transition-all duration-200 text-sm">
                                                        <Eye className="w-4 h-4" />
                                                        <span>View Details</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && wpData.length > 0 && (
                        <div className="px-6 py-4 border-t border-white/20 bg-white/5">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2 text-white/60 text-sm">
                                        <List className="w-4 h-4" />
                                        <span>Rows per page:</span>
                                    </div>
                                    <select value={pageSize} onChange={handlePageSizeChange}
                                        className="bg-white/10 border border-white/20 text-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                                        {[10, 25, 50, 100].map(n => <option key={n} value={n} className="bg-slate-800">{n}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button onClick={handlePreviousPage} disabled={currentPage === 1 || isLoading}
                                        className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                                        Previous
                                    </button>
                                    <span className="text-white/80 px-3 py-1 font-medium">Page {currentPage}</span>
                                    <button onClick={handleNextPage} disabled={wpData.length < pageSize || isLoading}
                                        className="px-3 py-1 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <WhatsAppDetails
                    selectedWp={selectedWp}
                    onClose={() => setSelectedWp(null)}
                    setError={setError}
                    loginId={loginId}
                    deliveryMode={deliveryMode}
                />
            </div>
        </div>
    );
}